import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileContract, faDownload, faSpinner, faEye } from '@fortawesome/free-solid-svg-icons';

import legalContractService from '../../services/legalContractService';
import PdfPreviewModal from '../shared/PdfPreviewModal';
import { useDocumentViewer } from '../DocumentsSplitPane';
import './ContractsInContextPanel.css';

// Only a PDF can be opened in the in-app viewer. Anything else keeps its
// Download button alone rather than offering an "open here" that always fails.
const isPdfFile = (filename) => {
  if (!filename) return false;
  return String(filename).split('.').pop().toLowerCase() === 'pdf';
};

/**
 * Read-only contracts panel, shared by the Enquiry, Delivery and CRM screens.
 *
 * Craig's visibility spec (2026-08-26): "Legal - no-one -> contracts visable
 * through Enq + Delivery under docs + CRM". The standalone Legal module is
 * closed, so this is where contracts are read now.
 *
 * ONE component for all three hosts on purpose. Three bespoke panels would
 * drift, and the drift would be invisible: each would still render, just with
 * a different idea of what a contract row shows or whether it can be
 * downloaded.
 *
 * Read-only by construction — there is no edit path here, and the backend
 * keeps every mutating route behind full access regardless.
 */
const ContractsInContextPanel = ({ enquiryId, deliveryId, partnerId, freightId, title, emptyLabel }) => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [previewingId, setPreviewingId] = useState(null);
  // Non-null only when this panel is rendered inside a DocumentsSplitPane.
  const viewer = useDocumentViewer();
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewName, setPreviewName] = useState('');

  const load = useCallback(async () => {
    if (!enquiryId && !deliveryId && !partnerId && !freightId) {
      setContracts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const rows = await legalContractService.inContext({ enquiryId, deliveryId, partnerId, freightId });
      setContracts(Array.isArray(rows) ? rows : []);
    } catch (err) {
      // Distinguish "none exist" from "we could not ask". An error rendered as
      // an empty list reads as "this deal has no contracts", which is the
      // failure mode that costs diagnosis time.
      setError('Could not load contracts. Please refresh.');
      setContracts([]);
    } finally {
      setLoading(false);
    }
  }, [enquiryId, deliveryId, partnerId, freightId]);

  useEffect(() => { load(); }, [load]);

  const handleDownload = async (contract, file) => {
    setDownloadingId(file.id);
    try {
      const blob = await legalContractService.downloadFile(contract.id, file.id);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.download = file.original_filename || `${contract.contract_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Could not download that file. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  // Read the contract next to the record instead of saving it to disk. Same
  // authenticated request the download already makes -- same route, same
  // row-level gate -- so this opens no access a reader did not already have.
  const handlePreview = async (contract, file) => {
    setPreviewingId(file.id);
    try {
      const blob = await legalContractService.downloadFile(contract.id, file.id);
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const name = file.original_filename || `${contract.contract_number}.pdf`;

      // Inside the delivery's split pane, show it THERE — beside the delivery,
      // which is the entire point of the pane. On a screen with no pane (the
      // Fuel Enquiry form hosts this same panel) ``viewer`` is null and the
      // pop-up is still the right answer: an inline viewer there would push
      // the enquiry's own fields off the screen.
      if (viewer) {
        viewer.open(url, name);
        // Held so closePreview can still revoke it, and so the local modal
        // stays shut: the pane owns what is on screen now, not this component.
        setPreviewUrl(url);
        setPreviewName(name);
        return;
      }

      setPreviewUrl(url);
      setPreviewName(name);
    } catch (err) {
      setError('Could not open that file. Please try again.');
    } finally {
      setPreviewingId(null);
    }
  };

  const closePreview = () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewName('');
  };

  if (loading) {
    return (
      <div className="contracts-in-context">
        <h3 className="cic-title"><FontAwesomeIcon icon={faFileContract} /> {title}</h3>
        <p className="cic-muted"><FontAwesomeIcon icon={faSpinner} spin /> Loading contracts…</p>
      </div>
    );
  }

  return (
    <div className="contracts-in-context">
      <h3 className="cic-title"><FontAwesomeIcon icon={faFileContract} /> {title}</h3>

      {error && <p className="cic-error">{error}</p>}

      {!error && contracts.length === 0 && (
        <p className="cic-muted">{emptyLabel}</p>
      )}

      {contracts.map((contract) => (
        <div key={contract.id} className="cic-row">
          <div className="cic-row-head">
            <span className="cic-number">{contract.contract_number}</span>
            <span className="cic-type">{contract.contract_type_display}</span>
            {contract.counterparty_name && (
              <span className="cic-counterparty">{contract.counterparty_name}</span>
            )}
          </div>
          <div className="cic-meta">
            {contract.entity_name && <span>{contract.entity_name}</span>}
            {contract.trade_recap_number && <span>{contract.trade_recap_number}</span>}
            {contract.freight_recap_number && <span>{contract.freight_recap_number}</span>}
            {contract.commence_date && <span>{contract.commence_date}</span>}
          </div>

          {(contract.files || []).length === 0 ? (
            <p className="cic-muted cic-nofile">No file attached.</p>
          ) : (
            <ul className="cic-files">
              {contract.files.map((file) => (
                <li key={file.id}>
                  {/* Reuses the existing download-link styling rather than
                      inventing a new one: two link buttons on one row that
                      look different read as two different KINDS of thing. */}
                  {isPdfFile(file.original_filename) && (
                    <button
                      type="button"
                      className="cic-download cic-preview"
                      style={{ marginRight: '0.6rem' }}
                      onClick={() => handlePreview(contract, file)}
                      disabled={previewingId === file.id}
                      title="Open contract here"
                      aria-label={`Open ${file.original_filename} here`}
                    >
                      <FontAwesomeIcon icon={previewingId === file.id ? faSpinner : faEye}
                                       spin={previewingId === file.id} />
                    </button>
                  )}
                  <button
                    type="button"
                    className="cic-download"
                    onClick={() => handleDownload(contract, file)}
                    disabled={downloadingId === file.id}
                  >
                    <FontAwesomeIcon icon={downloadingId === file.id ? faSpinner : faDownload}
                                     spin={downloadingId === file.id} />
                    {file.original_filename}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}

      <PdfPreviewModal
        isOpen={Boolean(previewUrl) && !viewer}
        pdfUrl={previewUrl}
        fileName={previewName}
        onClose={closePreview}
      />
    </div>
  );
};

ContractsInContextPanel.propTypes = {
  enquiryId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  deliveryId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  partnerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  freightId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  title: PropTypes.string,
  emptyLabel: PropTypes.string,
};

ContractsInContextPanel.defaultProps = {
  enquiryId: null,
  deliveryId: null,
  partnerId: null,
  freightId: null,
  title: 'Contracts',
  // The wording every host had before the delivery split screen existed, and
  // it stays that way. `emptyLabel` is a prop so a host can say something
  // truer for its own screen -- but a host that says nothing must keep the
  // words it already had. Moving this default is how a change made for the
  // delivery silently rewrites the enquiry screen, which is exactly what
  // happened once. Both live hosts now pass their own label, so this default
  // is a floor, not a decision.
  emptyLabel: 'No contracts yet.',
};

export default ContractsInContextPanel;
